import { useEffect, useState, useCallback } from "react";
import api, { fmtIDR, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { RefreshCw, Download, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { A1DetailDialog } from "@/components/payroll/A1DetailDialog";
import { downloadFile } from "@/components/payroll/dl";

const STATUS = { desember: ["Final Desember", "default"], resign: ["Final Resign", "destructive"], berjalan: ["Berjalan", "outline"] };
const COLS = [["gross", "Total Bruto"], ["biaya_jabatan", "Biaya Jabatan"], ["iuran_total", "Pengurang JHT + JP"], ["netto", "Neto"],
  ["ptkp_amount", "PTKP"], ["pkp", "PKP Setahun"], ["annual_pph", "PPh 21 Terutang"], ["pph_prev", "PPh 21 Sudah Dipotong"], ["pph_final", "PPh 21 Final"]];
const pad = (n) => String(n).padStart(2, "0");

function FinalRow({ r }) {
  const [label, variant] = STATUS[r.status];
  return (
    <TableRow data-testid={`final-row-${r.key}`}>
      <TableCell className="text-sm whitespace-nowrap"><span className="font-medium">{r.name}</span>
        <span className="block text-[10px] text-muted-foreground">{r.employee_code || "—"} · {r.nik || "tanpa NIK"}</span></TableCell>
      <TableCell className="text-xs">{r.ptkp_status}</TableCell>
      <TableCell className="whitespace-nowrap"><Badge variant={variant} className="text-[10px]">{label}</Badge>
        {r.needs_recalc && <span title="Baris masa terakhir belum dihitung ulang Pasal 17" className="inline-flex ml-1 text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /></span>}</TableCell>
      <TableCell className="num text-xs whitespace-nowrap">{pad(r.masa_awal)}–{pad(r.masa_akhir)} <span className="text-muted-foreground">({r.months} bln)</span></TableCell>
      {COLS.map(([k]) => <TableCell key={k} className={`num text-right text-xs ${k === "pph_final" ? "font-semibold" : ""} ${k === "pph_final" && r[k] < 0 ? "text-emerald-600" : ""}`} data-testid={`final-${k}-${r.key}`}>{k === "pph_final" && r.status === "berjalan" ? <span className="text-muted-foreground" title="Dihitung saat masa Desember / resign">—</span> : fmtIDR(r[k] || 0)}</TableCell>)}
      <TableCell><A1DetailDialog row={r} /></TableCell>
    </TableRow>
  );
}

export function FinalTab({ clientId, year }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data: d } = await api.get(`/payroll/annual?client_id=${clientId}&year=${year}`);
    setData(d);
  }, [clientId, year]);
  useEffect(() => { load(); }, [load]);
  const recalc = async () => {
    try { await api.post(`/payroll/recalculate-finals?client_id=${clientId}&year=${year}`); toast.success("PPh 21 masa terakhir dihitung ulang (Pasal 17)"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const dl = (path, name) => downloadFile(path, name).catch(() => toast.error("Gagal unduh"));
  const rows = data?.rows || [];
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={recalc} data-testid="final-recalc-btn"><RefreshCw className="h-4 w-4 mr-1" />Hitung Ulang Final</Button>
        <Button size="sm" variant="outline" onClick={() => dl(`/payroll/annual?client_id=${clientId}&year=${year}&fmt=xlsx`, `kalkulasi_akhir_a1_${year}.xlsx`)} data-testid="final-export-btn"><Download className="h-4 w-4 mr-1" />Detail Excel</Button>
        <Button size="sm" variant="outline" onClick={() => dl(`/payroll/coretax?client_id=${clientId}&type=bpa1&year=${year}`, `coretax_BPA1_${year}.xlsx`)} data-testid="final-bpa1-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Coretax BPA1</Button>
      </div>
      <p className="text-xs text-muted-foreground">Rekap per karyawan (ID / Nama + NIK sama otomatis digabung). Penghasilan neto tidak disetahunkan; PTKP penuh; PKP dibulatkan ribuan; PPh 21 Final = Terutang (Pasal 17) − Sudah Dipotong (TER masa sebelumnya). Negatif = lebih potong.</p>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead>Karyawan</TableHead><TableHead>PTKP</TableHead><TableHead>Status</TableHead><TableHead>Masa Kerja</TableHead>
            {COLS.map(([k, l]) => <TableHead key={k} className="text-right whitespace-nowrap">{l}</TableHead>)}<TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={14} className="text-center py-10 text-muted-foreground text-sm">Belum ada transaksi PPh 21 tahun {year}</TableCell></TableRow>}
            {rows.map((r) => <FinalRow key={r.key} r={r} />)}
            {rows.length > 0 && (
              <TableRow className="bg-secondary/50 font-semibold">
                <TableCell colSpan={4}>Total ({rows.length} karyawan)</TableCell>
                {COLS.map(([k]) => <TableCell key={k} className="num text-right text-xs">{data[`total_${k}`] !== undefined ? fmtIDR(data[`total_${k}`]) : ""}</TableCell>)}
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
